import { useContext } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { AuthContext } from '@/context/auth.context';
import useGetApi from 'src/api/useGetApi';

// Schema that defines how to order product attributes for selection UIs
// Keys are stable identifiers (prefer `customFieldId` over names)
export type ProductAttributeOrderConfig = {
  // Highest priority: specific products
  byProductId?: Record<string, string[]>; // productId -> ordered list of customFieldId
  // Fallback ordering to use when no product-specific or template-specific order exists
  global?: string[]; // ordered list of customFieldId
};

export interface OrganizationConfig {
  id: string;
  organizationId: string;
  organization: {
    name: string;
  };
  enabledModules: string[];
  logoUrl: string | null;
  // Short label injected into the browser tab title, e.g. "Gently (PRF) - Invoices"
  tabTitleLabel: string | null;
  timezone: string;
  defaultCurrency: string;
  // Optional attribute ordering schema surfaced from backend
  productAttributeOrder: ProductAttributeOrderConfig | null;
  // Recipients auto-CC'd on composed replies, per send category (base list =
  // general; suffixed = orders / discounts — strict, general is not a
  // fallback). Each id is a `comm_contacts` entry or a member user id.
  defaultCcRecipientIds: string[];
  defaultCcRecipientIdsOrders: string[];
  defaultCcRecipientIdsPurchaseOrders: string[];
  defaultCcRecipientIdsDiscounts: string[];
}

type ApiResponse = {
  organizationConfig: OrganizationConfig | null;
};

export const ORGANIZATION_CONFIG_QUERY_KEY = ['organization/config'] as const;

/**
 * Reads the already-fetched org config straight out of the query cache, for
 * call sites that run outside React — e.g. the root route's `head()`, which
 * TanStack evaluates while loading matches. Returns null when nothing is
 * cached yet; never triggers a fetch.
 */
export function readCachedOrganizationConfig(
  queryClient: QueryClient,
): OrganizationConfig | null {
  const cached = queryClient.getQueryData<ApiResponse>(
    ORGANIZATION_CONFIG_QUERY_KEY,
  );
  return cached?.organizationConfig ?? null;
}

const useOrganizationConfig = () => {
  const { currentUser } = useContext(AuthContext);

  return useGetApi<ApiResponse>({
    endpoint: 'organization/config',
    queryKey: ORGANIZATION_CONFIG_QUERY_KEY,
    options: {
      enabled: !!currentUser,
      gcTime: Infinity,
    },
  });
};

export default useOrganizationConfig;
